<template>
  <div class="min-h-screen bg-[#F5F5F7] dark:bg-black font-sans text-[#1d1d1f] dark:text-[#f5f5f7] flex flex-col">
    <div class="px-4 py-4 flex items-center justify-between sticky top-0 bg-[#F5F5F7]/80 dark:bg-black/80 backdrop-blur-xl z-20">
      <button @click="goBack" class="flex items-center gap-1 text-[#7B2CBF] font-medium text-[16px]">
        <span class="material-symbols-outlined text-[24px]">chevron_left</span>
        Voltar
      </button>
      <h1 class="text-[17px] font-bold absolute left-1/2 -translate-x-1/2">
        {{ isEditing ? 'Editar Tarefa' : 'Nova Tarefa' }}
      </h1>
      <button
        @click="submit"
        :disabled="saving"
        class="text-[#7B2CBF] font-bold text-[16px] disabled:opacity-50"
      >
        {{ saving ? 'Salvando...' : 'Salvar' }}
      </button>
    </div>

    <div class="flex-1 p-4 max-w-2xl mx-auto w-full">
      <form @submit.prevent="submit" class="space-y-6">
        <div
          class="bg-white dark:bg-[#1c1c1e] rounded-[18px] overflow-hidden shadow-sm border border-black/5 dark:border-white/5"
        >
          <div class="p-4 border-b border-[#e5e5ea] dark:border-[#38383a]">
            <input
              v-model.trim="form.nome"
              type="text"
              required
              class="w-full bg-transparent text-[17px] font-semibold placeholder-[#86868b] outline-none text-[#1d1d1f] dark:text-white"
              placeholder="Título da Tarefa"
            />
          </div>
          <div class="p-4">
            <textarea
              v-model="form.descricao"
              rows="4"
              class="w-full bg-transparent text-[15px] placeholder-[#86868b] outline-none text-[#1d1d1f] dark:text-white resize-none"
              placeholder="Adicione notas, detalhes ou links..."
            ></textarea>
          </div>
        </div>

        <div
          class="bg-white dark:bg-[#1c1c1e] rounded-[18px] overflow-hidden shadow-sm border border-black/5 dark:border-white/5 divide-y divide-[#e5e5ea] dark:divide-[#38383a]"
        >
          <div class="p-4 flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="h-8 w-8 rounded-[8px] bg-[#7B2CBF] flex items-center justify-center text-white">
                <span class="material-symbols-outlined text-[18px]">calendar_today</span>
              </div>
              <span class="text-[15px] font-medium">Data Limite</span>
            </div>
            <input
              v-model="form.data"
              type="date"
              class="bg-transparent text-[#86868b] text-[15px] outline-none text-right font-medium"
            />
          </div>

          <div class="p-4 flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="h-8 w-8 rounded-[8px] bg-[#FF9500] flex items-center justify-center text-white">
                <span class="material-symbols-outlined text-[18px]">flag</span>
              </div>
              <span class="text-[15px] font-medium">Prioridade</span>
            </div>
            <select
              v-model="form.prioridade"
              class="bg-transparent text-[#86868b] text-[15px] outline-none text-right font-medium appearance-none"
            >
              <option value="BAIXA">Baixa</option>
              <option value="MEDIA">Média</option>
              <option value="ALTA">Alta</option>
              <option value="URGENTE">Urgente</option>
            </select>
          </div>

          <div v-if="isEditing" class="p-4 flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="h-8 w-8 rounded-[8px] bg-[#34C759] flex items-center justify-center text-white">
                <span class="material-symbols-outlined text-[18px]">check_circle</span>
              </div>
              <span class="text-[15px] font-medium">Status</span>
            </div>
            <select
              v-model="form.status"
              class="bg-transparent text-[#86868b] text-[15px] outline-none text-right font-medium appearance-none"
            >
              <option value="A_FAZER">A Fazer</option>
              <option value="EM_ANDAMENTO">Em Andamento</option>
              <option value="CONCLUIDO">Concluído</option>
            </select>
          </div>
        </div>

        <p class="text-[13px] text-[#86868b] px-2 text-center">
          Todas as alterações são sincronizadas automaticamente.
        </p>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'

const route = useRoute()
const router = useRouter()
const apiUrl = 'https://backend.controlease.com.br/tarefas'
const authConfig = () => {
  const token = localStorage.getItem('token')
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {}
}

const saving = ref(false)
const isEditing = computed(() => !!route.params.id)
const form = ref({
  id: null,
  nome: '',
  descricao: '',
  data: new Date().toISOString().slice(0, 10),
  status: 'A_FAZER',
  prioridade: 'MEDIA'
})

const loadTask = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/${route.params.id}`, authConfig())
    form.value = {
      ...data,
      status: data.status ?? 'A_FAZER',
      prioridade: data.prioridade ?? 'MEDIA'
    }
  } catch (e) {
    router.push({ name: 'TarefasUser' })
  }
}

const submit = async () => {
  if (saving.value) return
  saving.value = true
  try {
    const payload = { ...form.value }
    if (isEditing.value) {
      await axios.put(`${apiUrl}/${payload.id}`, payload, authConfig())
    } else {
      await axios.post(apiUrl, payload, authConfig())
    }
    router.push({ name: 'TarefasUser' })
  } catch (e) {
    alert('Erro ao salvar')
  } finally {
    saving.value = false
  }
}

const goBack = () => {
  router.push({ name: 'TarefasUser' })
}

onMounted(() => {
  if (!isEditing.value) {
    const { status, prioridade } = route.query || {}
    if (status) form.value.status = status
    if (prioridade) form.value.prioridade = prioridade
  } else {
    loadTask()
  }
})
</script>

<style scoped>
.font-sans {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, Helvetica,
    Arial, sans-serif;
}
</style>
